import {
  getAmoritizationNumberPayments,
  sbaLoanAmortizationPaymentsAtom,
  sbaLoanAmortizationScheduleAtom,
  sbaLoanNumberOfPaymentsPerYearAtom,
  sbaLoanRecurringPaymentAtom,
  sellersNoteAmortizationPaymentsAtom,
  sellersNoteNumberOfPaymentsPerYearAtom,
  sellersNoteRecurringPaymentAtom,
} from "@/financeModels/owasco/amortization";
import {
  isConventionalLoanTypeAtom,
  sbaLoanConventionalTermLengthAtom,
  sbaLoanInterestRateAtom,
  sbaLoanStartDateAtom,
  sbaLoanTotalWithOptionalFeesAtom,
  sellersNoteAmortizationScheduleAtom,
  sellersNoteDollarAtom,
  sellersNoteInterestRateAtom,
  sellersNoteStartDateAtom,
  sellersNoteTermLengthAtom,
} from "@/financeModels/owasco/general";
import { toNumberOrNull } from "@/utils/math";
import { Getter, atom } from "jotai";
import { boldStyle, createEmptyRow, createTitleRow } from "./helper";

const XLSXStub = () => import("xlsx-js-style");
type XLSXModule = Awaited<ReturnType<typeof XLSXStub>>;

const currencyFormat = '"$"#,##0;[Red]("$"#,##0)';
const percentFormat = "0.00%";

const stringCell = (value: string, style?: Record<string, unknown>) => ({
  v: value,
  t: "s",
  s: style,
});

const numberCell = (
  value: unknown,
  format: string,
  style?: Record<string, unknown>,
) => {
  const numericValue = Number(value);

  if (value === null || value === undefined || Number.isNaN(numericValue)) {
    return stringCell("", style);
  }

  return {
    v: Number(numericValue.toFixed(6)),
    t: "n",
    z: format,
    s: style,
  };
};

const currencyCell = (value: unknown, style?: Record<string, unknown>) =>
  numberCell(value, currencyFormat, style);

const percentCell = (value: unknown, style?: Record<string, unknown>) =>
  numberCell(value, percentFormat, style);

const dateCell = (
  value: Date | null | undefined,
  style?: Record<string, unknown>,
) => {
  if (!value) {
    return stringCell("", style);
  }

  return stringCell(value.toLocaleDateString(), style);
};

const buildLoanDetailsRows = ({
  title,
  loanAmount,
  interestRate,
  schedule,
  paymentsPerYear,
  loanStartDate,
  payments,
  recurringExtraPayment,
  termLength,
}: {
  title: string;
  loanAmount: number;
  interestRate: number;
  schedule: number;
  paymentsPerYear: number;
  loanStartDate: Date;
  payments: {
    date: Date;
    fixedExtraPayment: number;
    scheduledPayment: number;
    cumulativeInterest: number;
  }[];
  recurringExtraPayment: number;
  termLength: number | null;
}) => {
  const yearCount = Math.floor(schedule || 0);
  const monthCount = Math.round(((schedule || 0) % 1) * 12);
  const loanEndDate = payments.at(-1)?.date;
  const plannedScheduledPaymentsCount = getAmoritizationNumberPayments({
    schedule: termLength || schedule || 0,
    paymentPerYear: paymentsPerYear || 0,
  });
  const totalExtraPayments = payments.reduce(
    (acc, payment) => acc + (payment.fixedExtraPayment || 0),
    0,
  );

  const rows = [
    createTitleRow(title),
    createEmptyRow(),
    [stringCell("Loan Details", boldStyle), stringCell("Value", boldStyle)],
    [stringCell("Loan Amount"), currencyCell(loanAmount)],
    [stringCell("Interest Rate"), percentCell(interestRate)],
  ];

  if (termLength === null) {
    rows.push([
      stringCell("Loan Period"),
      stringCell(
        `${yearCount} years${monthCount > 0 ? `, ${monthCount} months` : ""}`,
      ),
    ]);
  } else {
    rows.push([stringCell("Term Length"), stringCell(`${termLength} years`)]);
  }

  rows.push([stringCell("Loan Start Date"), dateCell(loanStartDate)]);
  rows.push([stringCell("Loan Payoff Date"), dateCell(loanEndDate)]);
  rows.push(createEmptyRow());

  rows.push([
    stringCell("Repayment Summary", boldStyle),
    stringCell("Value", boldStyle),
  ]);
  rows.push([
    stringCell("Scheduled Monthly Payment"),
    currencyCell(Number(payments.at(-2)?.scheduledPayment || 0)),
  ]);
  rows.push([
    stringCell("Scheduled Periods"),
    stringCell(`${plannedScheduledPaymentsCount} monthly payments`),
  ]);
  rows.push([
    stringCell("Actual Number of Payments"),
    stringCell(`${payments.length} monthly payments`),
  ]);
  rows.push([
    stringCell("Recurring Extra Payment"),
    currencyCell(recurringExtraPayment),
  ]);
  rows.push([
    stringCell("Total Extra Payments"),
    currencyCell(totalExtraPayments),
  ]);
  rows.push([
    stringCell("Total Interest"),
    currencyCell(payments.at(-1)?.cumulativeInterest || 0),
  ]);
  rows.push(createEmptyRow());

  return rows;
};

const buildAmortizationTableRows = (
  payments: {
    paymentPeriod: number;
    date: Date;
    yearPeriod: number;
    fixedExtraPayment: number;
    totalPayment: number;
    scheduledPayment: number;
    principal: number;
    interest: number;
    endingBalance: number;
    cumulativeInterest: number;
  }[],
) => {
  const rows: unknown[][] = [
    [
      stringCell("Payment #", boldStyle),
      stringCell("Date", boldStyle),
      stringCell("Year", boldStyle),
      stringCell("Extra Payment", boldStyle),
      stringCell("Total Payment", boldStyle),
      stringCell("Scheduled Payment", boldStyle),
      stringCell("Principal", boldStyle),
      stringCell("Interest", boldStyle),
      stringCell("Ending Balance", boldStyle),
      stringCell("Cumulative Interest", boldStyle),
    ],
  ];

  payments.forEach((payment) => {
    rows.push([
      numberCell(payment.paymentPeriod, "0"),
      dateCell(payment.date),
      numberCell(payment.yearPeriod, "0"),
      currencyCell(payment.fixedExtraPayment),
      currencyCell(payment.totalPayment),
      currencyCell(payment.scheduledPayment),
      currencyCell(payment.principal),
      currencyCell(payment.interest),
      currencyCell(payment.endingBalance),
      currencyCell(payment.cumulativeInterest),
    ]);
  });

  return rows;
};

export const buildAmortizationWorksheet = (get: Getter, XLSX: XLSXModule) => {
  const isConventionalLoanType = get(isConventionalLoanTypeAtom);

  const pageTitle = (window.document.title || "ETA IQ Amortization")
    .replace("Model (", "")
    .replace(")", "");

  const sbaTitle = isConventionalLoanType ? "SENIOR DEBT" : "SBA 7(A) LOAN";
  const sbaPayments = get(sbaLoanAmortizationPaymentsAtom);
  const sellersPayments = get(sellersNoteAmortizationPaymentsAtom);

  const rows = [
    [pageTitle],
    ["Created on - " + new Date().toLocaleString()],
    createEmptyRow(),

    ...buildLoanDetailsRows({
      title: `${sbaTitle} - AMORTIZATION`,
      loanAmount: toNumberOrNull(get(sbaLoanTotalWithOptionalFeesAtom)) || 0,
      interestRate: toNumberOrNull(get(sbaLoanInterestRateAtom)) || 0,
      schedule: toNumberOrNull(get(sbaLoanAmortizationScheduleAtom)) || 0,
      paymentsPerYear: get(sbaLoanNumberOfPaymentsPerYearAtom) || 0,
      loanStartDate: get(sbaLoanStartDateAtom).toDate(),
      payments: sbaPayments,
      recurringExtraPayment:
        toNumberOrNull(get(sbaLoanRecurringPaymentAtom)) || 0,
      termLength: isConventionalLoanType
        ? Number(get(sbaLoanConventionalTermLengthAtom)) || 0
        : null,
    }),

    ...buildAmortizationTableRows(sbaPayments),
    createEmptyRow(),
    createEmptyRow(),

    ...buildLoanDetailsRows({
      title: "SELLER'S NOTE - AMORTIZATION",
      loanAmount: toNumberOrNull(get(sellersNoteDollarAtom)) || 0,
      interestRate: toNumberOrNull(get(sellersNoteInterestRateAtom)) || 0,
      schedule: toNumberOrNull(get(sellersNoteAmortizationScheduleAtom)) || 0,
      paymentsPerYear: get(sellersNoteNumberOfPaymentsPerYearAtom) || 0,
      loanStartDate: get(sellersNoteStartDateAtom).toDate(),
      payments: sellersPayments,
      recurringExtraPayment:
        toNumberOrNull(get(sellersNoteRecurringPaymentAtom)) || 0,
      termLength: Number(get(sellersNoteTermLengthAtom)) || null,
    }),

    ...buildAmortizationTableRows(sellersPayments),
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);

  ws["!cols"] = [
    { wpx: 160 },
    { wpx: 150 },
    { wpx: 80 },
    { wpx: 130 },
    { wpx: 130 },
    { wpx: 140 },
    { wpx: 130 },
    { wpx: 130 },
    { wpx: 150 },
    { wpx: 150 },
  ];

  return ws;
};

export const amortizationExportAtom = atom(null, async (get) => {
  const XLSX = await XLSXStub();
  const ws = buildAmortizationWorksheet(get, XLSX);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Amortization");

  const fmt = "xlsx";
  const filename = `amortization-${new Date()
    .toISOString()
    .replace(/:/g, "-")
    .replace(/\..+/, "")}.${fmt}`;

  XLSX.writeFile(wb, filename);

  return;
});
